/**
 * Formula Expression evaluator + BTABLE batch pipeline (DYN-4.1).
 *
 * Consumes the AST produced by `formulaParser.ts` and evaluates it
 * against a flat variable scope. The evaluator is safe: no `eval`, no
 * `Function()`, no prototype access. All identifiers resolve through
 * the provided `scope` or the whitelisted `Math.*` namespace.
 *
 * Additionally exposes `evaluateFormulaBatch` which topologically
 * orders a set of Block Properties Table entries by dependency, applies
 * circular-reference detection, and produces a values/errors report for
 * the whole batch at once — what the evaluator pipeline in
 * `evaluator.ts` calls once per INSERT for BTABLE formula support.
 */

import {
  collectDependencies,
  parseFormula,
  type FormulaAST,
  type FormulaValue,
} from "./formulaParser";

export type { FormulaValue } from "./formulaParser";

export class FormulaEvalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FormulaEvalError";
  }
}

// ═══════════════════════════════════════════════════════════════════
// Whitelisted Math.* namespace
// ═══════════════════════════════════════════════════════════════════

const MATH_CONSTANTS: Readonly<Record<string, number>> = {
  PI: Math.PI,
  E: Math.E,
  LN2: Math.LN2,
  LN10: Math.LN10,
  LOG2E: Math.LOG2E,
  LOG10E: Math.LOG10E,
  SQRT2: Math.SQRT2,
};

type MathFn = (args: readonly number[]) => number;

const MATH_FUNCTIONS: Readonly<Record<string, MathFn>> = {
  sin: ([x]) => Math.sin(x!),
  cos: ([x]) => Math.cos(x!),
  tan: ([x]) => Math.tan(x!),
  asin: ([x]) => Math.asin(x!),
  acos: ([x]) => Math.acos(x!),
  atan: ([x]) => Math.atan(x!),
  atan2: ([y, x]) => Math.atan2(y!, x!),
  sqrt: ([x]) => Math.sqrt(x!),
  abs: ([x]) => Math.abs(x!),
  floor: ([x]) => Math.floor(x!),
  ceil: ([x]) => Math.ceil(x!),
  round: ([x]) => Math.round(x!),
  trunc: ([x]) => Math.trunc(x!),
  sign: ([x]) => Math.sign(x!),
  log: ([x]) => Math.log(x!),
  log2: ([x]) => Math.log2(x!),
  log10: ([x]) => Math.log10(x!),
  exp: ([x]) => Math.exp(x!),
  pow: ([a, b]) => Math.pow(a!, b!),
  min: (args) => Math.min(...args),
  max: (args) => Math.max(...args),
  hypot: (args) => Math.hypot(...args),
};

export type FormulaScope = Readonly<Record<string, FormulaValue>>;

// ═══════════════════════════════════════════════════════════════════
// Coercion helpers
// ═══════════════════════════════════════════════════════════════════

function asNumber(v: FormulaValue, op: string): number {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  throw new FormulaEvalError(
    `Cannot apply ${op} to non-numeric value ${JSON.stringify(v)}`,
  );
}

function asBoolean(v: FormulaValue): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") return v.length > 0;
  return false;
}

function valuesEqual(a: FormulaValue, b: FormulaValue): boolean {
  if (typeof a === typeof b) return a === b;
  if (typeof a === "number" && typeof b === "string") {
    const n = Number(b);
    return Number.isFinite(n) && n === a;
  }
  if (typeof a === "string" && typeof b === "number") {
    const n = Number(a);
    return Number.isFinite(n) && n === b;
  }
  return a === b;
}

function compareNumeric(
  a: FormulaValue,
  b: FormulaValue,
  op: "<" | ">" | "<=" | ">=",
): boolean {
  const na = asNumber(a, op);
  const nb = asNumber(b, op);
  switch (op) {
    case "<":
      return na < nb;
    case ">":
      return na > nb;
    case "<=":
      return na <= nb;
    case ">=":
      return na >= nb;
  }
}

/**
 * Forbidden identifier list — rejected even if the scope accidentally
 * contains such a key. Prevents traversal of host-JS prototype chain
 * via names like `__proto__`, `constructor`, `prototype`.
 */
const FORBIDDEN_IDENTIFIERS = new Set<string>([
  "__proto__",
  "constructor",
  "prototype",
  "Function",
  "eval",
  "globalThis",
  "window",
  "global",
  "self",
]);

function isForbiddenPath(path: readonly string[]): boolean {
  for (const segment of path) {
    if (FORBIDDEN_IDENTIFIERS.has(segment)) return true;
  }
  return false;
}

function resolveVariablePath(
  path: readonly string[],
  scope: FormulaScope,
): FormulaValue {
  if (path.length === 0) {
    throw new FormulaEvalError("Empty variable path");
  }
  if (isForbiddenPath(path)) {
    throw new FormulaEvalError(`Forbidden identifier: ${path.join(".")}`);
  }
  if (path[0] === "Math" && path.length === 2) {
    const name = path[1]!;
    const constant = MATH_CONSTANTS[name];
    if (constant !== undefined) return constant;
    throw new FormulaEvalError(`Unknown Math constant: ${name}`);
  }
  if (path.length === 1) {
    const name = path[0]!;
    // Use Object.hasOwn to avoid prototype-chain lookup. Falls back to
    // hasOwnProperty for older runtimes (should not happen in our
    // TS target, but cheap belt-and-suspenders).
    const hasIt =
      typeof Object.hasOwn === "function"
        ? Object.hasOwn(scope, name)
        : Object.prototype.hasOwnProperty.call(scope, name);
    if (!hasIt) {
      throw new FormulaEvalError(`Unknown variable: ${name}`);
    }
    return scope[name]!;
  }
  // Dotted scope key lookup — the evaluator treats "foo.x" as a flat key.
  const joined = path.join(".");
  const hasIt =
    typeof Object.hasOwn === "function"
      ? Object.hasOwn(scope, joined)
      : Object.prototype.hasOwnProperty.call(scope, joined);
  if (hasIt) {
    return scope[joined]!;
  }
  throw new FormulaEvalError(`Unknown variable: ${joined}`);
}

function invokeFunction(
  path: readonly string[],
  args: readonly FormulaValue[],
): FormulaValue {
  if (path.length === 2 && path[0] === "Math") {
    const name = path[1]!;
    const fn = MATH_FUNCTIONS[name];
    if (!fn) {
      throw new FormulaEvalError(`Unknown Math function: ${name}`);
    }
    const numArgs = args.map((a, i) =>
      asNumber(a, `Math.${name}() argument ${i + 1}`),
    );
    return fn(numArgs);
  }
  throw new FormulaEvalError(
    `Unknown function: ${path.join(".")}. Only Math.* is supported.`,
  );
}

/**
 * Convert a boolean to its numeric counterpart when concatenated with a
 * string — keeps AutoCAD parity where `"W" + true` → "W1" (not "Wtrue").
 */
function formatForConcat(v: FormulaValue): FormulaValue {
  if (typeof v === "boolean") return v ? 1 : 0;
  return v;
}

// ═══════════════════════════════════════════════════════════════════
// Core evaluator
// ═══════════════════════════════════════════════════════════════════

/**
 * Evaluate a parsed formula AST against a flat variable scope.
 *
 * The scope is a plain record of name → value. Dotted variable access
 * (`foo.bar`) looks up the joined key `foo.bar` in the scope. The
 * Math.* namespace is reserved for the whitelisted functions/constants
 * and never collides with scope keys.
 */
export function evaluateFormula(
  ast: FormulaAST,
  scope: FormulaScope,
): FormulaValue {
  switch (ast.kind) {
    case "number":
      return ast.value;
    case "string":
      return ast.value;
    case "boolean":
      return ast.value;
    case "variable":
      return resolveVariablePath(ast.path, scope);
    case "unary": {
      const operand = evaluateFormula(ast.operand, scope);
      switch (ast.op) {
        case "-":
          return -asNumber(operand, "unary -");
        case "+":
          return +asNumber(operand, "unary +");
        case "!":
          return !asBoolean(operand);
      }
      break;
    }
    case "binary": {
      // Short-circuit boolean ops evaluate right-hand only if required.
      if (ast.op === "&&" || ast.op === "||") {
        const left = evaluateFormula(ast.left, scope);
        const lb = asBoolean(left);
        if (ast.op === "&&") {
          if (!lb) return false;
          return asBoolean(evaluateFormula(ast.right, scope));
        }
        if (lb) return true;
        return asBoolean(evaluateFormula(ast.right, scope));
      }
      const left = evaluateFormula(ast.left, scope);
      const right = evaluateFormula(ast.right, scope);
      switch (ast.op) {
        case "+": {
          if (typeof left === "string" || typeof right === "string") {
            return (
              String(formatForConcat(left)) + String(formatForConcat(right))
            );
          }
          return asNumber(left, "+") + asNumber(right, "+");
        }
        case "-":
          return asNumber(left, "-") - asNumber(right, "-");
        case "*":
          return asNumber(left, "*") * asNumber(right, "*");
        case "/": {
          const divisor = asNumber(right, "/");
          if (divisor === 0) {
            throw new FormulaEvalError("Division by zero");
          }
          return asNumber(left, "/") / divisor;
        }
        case "%": {
          const divisor = asNumber(right, "%");
          if (divisor === 0) {
            throw new FormulaEvalError("Modulo by zero");
          }
          return asNumber(left, "%") % divisor;
        }
        case "**":
          return Math.pow(asNumber(left, "**"), asNumber(right, "**"));
        case "==":
          return valuesEqual(left, right);
        case "!=":
          return !valuesEqual(left, right);
        case "<":
        case ">":
        case "<=":
        case ">=":
          return compareNumeric(left, right, ast.op);
      }
      break;
    }
    case "ternary": {
      const cond = evaluateFormula(ast.condition, scope);
      return asBoolean(cond)
        ? evaluateFormula(ast.then, scope)
        : evaluateFormula(ast.else, scope);
    }
    case "call": {
      const args = ast.args.map((a) => evaluateFormula(a, scope));
      return invokeFunction(ast.name, args);
    }
  }
  throw new FormulaEvalError(
    `Unhandled AST node kind: ${(ast as { kind: string }).kind}`,
  );
}

// ═══════════════════════════════════════════════════════════════════
// BTABLE batch evaluation — topological order + cycle detection
// ═══════════════════════════════════════════════════════════════════

export interface FormulaEntry {
  readonly key: string;
  readonly formula?: string;
  readonly staticValue?: FormulaValue;
}

export interface FormulaBatchResult {
  readonly values: Readonly<Record<string, FormulaValue>>;
  readonly errors: readonly string[];
  /** Keys whose formula produced an error (no value emitted). */
  readonly failedKeys: readonly string[];
}

/**
 * Topologically evaluate a batch of BTABLE entries — entries with
 * formulas are evaluated in dependency order, using the `baseScope` for
 * references outside the batch (parameter runtime values + static
 * BTABLE defaults). Cycle detection reports the offending keys without
 * throwing.
 *
 * Rules:
 *   - Entries with neither formula nor staticValue ⇒ skipped.
 *   - Formula parse error ⇒ recorded, key excluded from values.
 *   - Circular reference ⇒ reported for every cycle member.
 *   - Unresolved dependency (formula refers to unknown identifier) ⇒
 *     recorded, key excluded from values.
 *
 * The returned `values` map is keyed by entry key.
 */
export function evaluateFormulaBatch(
  entries: readonly FormulaEntry[],
  baseScope: FormulaScope,
): FormulaBatchResult {
  const errors: string[] = [];
  const failedKeys: string[] = [];
  const values: Record<string, FormulaValue> = {};

  interface Parsed {
    readonly entry: FormulaEntry;
    readonly ast: FormulaAST;
    readonly dependencies: Set<string>;
  }
  const parsed: Parsed[] = [];
  // Only entries that have a formula participate in dependency ordering.
  // Static-only entries are eagerly written to `values` (they don't
  // need ordering — their dependents read them like baseScope keys).
  const formulaKeys = new Set<string>();
  for (const entry of entries) {
    if (entry.formula) formulaKeys.add(entry.key);
  }

  for (const entry of entries) {
    if (!entry.formula && entry.staticValue === undefined) {
      continue;
    }
    if (!entry.formula) {
      values[entry.key] = entry.staticValue as FormulaValue;
      continue;
    }
    try {
      const ast = parseFormula(entry.formula);
      const deps = collectDependencies(ast);
      // Only dependencies on OTHER formula-bearing entries in the batch
      // participate in ordering — references to static entries are
      // resolved via the values map (already populated) and external
      // references via baseScope.
      const batchDeps = new Set<string>();
      for (const d of deps) {
        if (formulaKeys.has(d) && d !== entry.key) batchDeps.add(d);
      }
      parsed.push({ entry, ast, dependencies: batchDeps });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`Formula parse error in "${entry.key}": ${msg}`);
      failedKeys.push(entry.key);
    }
  }

  // Kahn's algorithm — topological sort.
  const ordered: Parsed[] = [];
  const inDegree = new Map<string, number>();
  for (const p of parsed) {
    inDegree.set(p.entry.key, p.dependencies.size);
  }
  const queue: Parsed[] = [];
  for (const p of parsed) {
    if ((inDegree.get(p.entry.key) ?? 0) === 0) queue.push(p);
  }
  while (queue.length > 0) {
    const p = queue.shift()!;
    ordered.push(p);
    for (const q of parsed) {
      if (q.dependencies.has(p.entry.key)) {
        const next = (inDegree.get(q.entry.key) ?? 0) - 1;
        inDegree.set(q.entry.key, next);
        if (next === 0) queue.push(q);
      }
    }
  }
  if (ordered.length < parsed.length) {
    const cycleKeys = parsed
      .filter((p) => !ordered.includes(p))
      .map((p) => p.entry.key);
    for (const k of cycleKeys) {
      errors.push(`Circular reference in formula "${k}"`);
      failedKeys.push(k);
    }
  }

  for (const p of ordered) {
    const scope: Record<string, FormulaValue> = { ...baseScope, ...values };
    try {
      const v = evaluateFormula(p.ast, scope);
      values[p.entry.key] = v;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      errors.push(`Formula eval error in "${p.entry.key}": ${msg}`);
      failedKeys.push(p.entry.key);
    }
  }

  return { values, errors, failedKeys };
}
